import { useCallback, useEffect, useState } from 'react';
import FileAnalyticsAPI from './fileAnalyticsService';
import useCmLiveProgress from './useCmLiveProgress';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Whether Continuous Monitoring is busy for this entity, so a screen can stop
 * offering actions that would collide with a run in progress.
 *
 * This reuses the same live connection the results screen uses, so knowing
 * whether a run is on costs one reading when the screen opens and nothing
 * thereafter — the server says when a run starts and when it ends.
 *
 * The screen lock is a courtesy, not the protection. The server refuses
 * conflicting work on its own: a source is marked as locked for the duration of
 * a run and the run endpoints answer 423 while it is. Greying out a button
 * simply saves someone from pressing it and reading an error.
 */
export function useCmRunLock() {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState<string | null>(null);
  const [sourceName, setSourceName] = useState<string | null>(null);

  const { state: authState } = useAuth();
  const entityKey = (authState.user as any)?.entity_id
    ? String((authState.user as any).entity_id)
    : null;

  const refresh = useCallback(async () => {
    try {
      const s = await FileAnalyticsAPI.entityStatus();
      setRunning(s.running);
      setPhase(s.running ? (s.phase ?? 'Running') : null);
      setSourceName(s.running ? (s.source_name ?? null) : null);
    } catch {
      // Not being able to tell is not worth interrupting anyone. The server
      // still refuses conflicting work regardless of what the screen believes.
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useCmLiveProgress(entityKey, {
    // A whole-entity run and a single-source run both lock the screen. Only the
    // outermost start and finish change the locked state, so a run moving from
    // one source to the next does not unlock in between.
    onBatchStarted: () => { setRunning(true); setPhase('Preparing'); },
    onBatchFinished: () => { setRunning(false); setPhase(null); setSourceName(null); },
    onSourceStarted: (e) => { setRunning(true); setSourceName(e.source_name); },
    onRunStarted: () => { setRunning(true); setPhase('Running detection rules'); },
    // Announcements sent while the connection was away are gone, so the state is
    // read again rather than assumed still correct.
    onReconnect: refresh,
  });

  /** A reason to show on a disabled control, or null when nothing is running. */
  const lockReason = running
    ? `A run is in progress${sourceName ? ` on ${sourceName}` : ''} — please wait for it to finish`
    : null;

  return { running, phase, sourceName, lockReason, refresh };
}

export default useCmRunLock;
